import { getCookie, setCookie } from "./cookie";
import customAlert from "./customAlert";

const PUBLIC_VAPID_KEY =
  "BMI4fBPvbqACYkqdbuBT90oyAbtEOHbZNx_g4Qdi5fEO7Kn-dwpd-4feSyyheBRc76nbHilmO6VJFm39f8qEF50";

/** base64 → Uint8Array 변환 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Safari 환경 감지 */
function isIOSSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS/.test(ua) && /iPhone|iPad|iPod/.test(ua);
}
function isMacOSSafari(): boolean {
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome|CriOS/.test(ua) && /Macintosh/.test(ua);
}

/** Service Worker 등록 */
async function registerServiceWorker() {
  const register = await navigator.serviceWorker.register("/sw.js");
  console.log("✅ Service Worker 등록 완료:", register.scope);
  return register;
}

/** 푸시 구독 시도 및 서버 등록 */
async function tryPushSubscribe(register: ServiceWorkerRegistration) {
  try {
    const subscription = await register.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(PUBLIC_VAPID_KEY) as unknown as BufferSource,
    });

    let input = `/api/push/subscribe/`;
    if (process.env.NODE_ENV === "development") {
      const baseUrl = process.env.NEXT_PUBLIC_API_BASE as string;
      input = baseUrl + input
    }

    // 서버에 전송
    await fetch(input, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(subscription),
    });

    console.log("📡 푸시 구독 성공:", subscription);
    return true;
  } catch (err) {
    console.warn("❌ 푸시 구독 실패:", err);
    return false;
  }
}

/** 푸시 등록 로직 */
async function registPushAlert() {
  try {
    const register = await registerServiceWorker();
    const success = await tryPushSubscribe(register);

    if (success) {
      customAlert(a("pushRegistered"), "success");
    } else {
      customAlert(a("pushRegistrationFailed"), "error");
    }
  } catch (err) {
    console.error("푸시 등록 중 오류:", err);
    customAlert(a("pushRegistrationError"), "error");
  }
}

/** 메인 함수 */
export async function registerPush(): Promise<void> {
  const getPushExpired = getCookie('REGISTER_SW_EXPIERD')
  // 값이 존재하면 시도 하지 않음
  if (getPushExpired) return;
  // 값이 없으면 만료되었으므로 다시 설정 하고 진행
  // const expiers = new Date(Date.now() + 60 * 1000)              // 1분
  // const expiers = new Date(Date.now() + 60 * 60 * 1000)      // 1시간
  const expiers = new Date(Date.now() + 24 * 60 * 60 * 1000) // 24시간
  setCookie('REGISTER_SW_EXPIERD', "1", { path: "/", expires: expiers })

  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("❌ 푸시를 지원하지 않는 브라우저입니다.");
    return;
  }

  const isIOS = isIOSSafari();
  const isMac = isMacOSSafari();

  const register = await registerServiceWorker();
  const existing = await register.pushManager.getSubscription();

  // ✅ 이미 구독된 상태
  if (existing) {
    console.log("✅ 이미 푸시 구독된 상태입니다.");
    return;
  }

  // ✅ iOS Safari (신뢰 가능한 방식: 구독 시도로 판단)
  if (isIOS) {
    console.log("📱 iOS Safari 환경 감지 — 실제 구독 시도로 권한 확인");
    const success = await tryPushSubscribe(register);

    if (!success) {
      customAlert(
        "iOS Safari에서는 보안 정책상 수동으로 푸시 등록이 필요합니다.",
        "warning",
        () => registPushAlert()
      );
    }
    return;
  }

  // ✅ macOS Safari — Website Push ID 방식 (사용자 수동 등록)
  if (isMac) {
    console.log("🧭 macOS Safari 환경 감지 — 수동 등록 필요");
    customAlert(
      "macOS Safari에서는 보안 정책상 수동으로 푸시 알림을 등록해야 합니다.",
      "warning",
      () => registPushAlert()
    );
    return;
  }

  // ✅ 그 외 브라우저 (Chrome, Edge, Firefox 등)
  const permission = await Notification.requestPermission();

  if (permission !== "granted") {
    customAlert(a("notificationPermissionDenied"), "warning");
    return;
  }

  const success = await tryPushSubscribe(register);
  if (success) {
    customAlert(a('pushRegistered'), "success");
  } else {
    customAlert(a("pushRegistrationFailedSimple"), "error");
  }
}



// navigator.serviceWorker.ready.then(reg => {
//   console.log("🔍 활성화된 Service Worker:", reg.active?.scriptURL);
// });

function a(key: string){
  const getLang = getCookie("lang") ?? "ko"

  const transMessages: Record<string, any> = {
    "ko": {      
      "pushRegistered": "푸시 등록이 완료되었습니다.",
      "pushRegistrationFailed": "푸시 등록에 실패했습니다. 권한을 다시 확인해주세요.",
      "pushRegistrationError": "푸시 등록 중 오류가 발생했습니다.",
      "notificationPermissionDenied": "알림 권한이 허용되지 않았습니다.",
      "pushRegistrationFailedSimple": "푸시 등록에 실패했습니다.",
    },
    "en": {      
        "pushRegistered": "Push registration completed.",
        "pushRegistrationFailed": "Push registration failed. Please check your permissions.",
        "pushRegistrationError": "An error occurred during push registration.",
        "notificationPermissionDenied": "Notification permission is not granted.",
        "pushRegistrationFailedSimple": "Push registration failed.",
    },
  }

  const isLangHere = transMessages[getLang]
  if(isLangHere && isLangHere[key]){
    return isLangHere[key]
  }else{
    return "";
  }
}